import { LoadFailed, QueryLoading } from '../components/QueryState';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Panel, PanelHeader } from '@promanage/ui';
import { ROLE_LABELS } from '@promanage/contracts/person-display';
import { formatPeriod, nzDate, periodStartFor } from '@promanage/core';
import { ReportWorkspace } from '../features/reports/ReportWorkspace';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { OwnerStatementReport } from '../features/reports/OwnerStatementReport';
import { StatementPicker } from '../features/reports/StatementPicker';
import { statementNumberLabel } from '@promanage/documents/owner-statement';
import { parseStatementSearch } from '../features/reports/statementSearch';
import { letterheadLogo } from '../features/branding/letterhead';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/reports/statements')({
  // Shape-checked only — see `parseStatementSearch`. A month this agency has no books for is
  // answered by an empty statement, which is a truthful document, not an error.
  validateSearch: parseStatementSearch,
  component: OwnerStatementsPage,
});

/**
 * Owner statements.
 *
 * WHAT IT IS FOR. An agency holds other people's money, and once a month it has to tell each of
 * them what it did with theirs. Everything needed to answer that has been in the ledger since M1
 * — the owner's share of every receipt, the fee charged out of it, the money paid away — and
 * there has been no way to produce the one document the owner reads. This is the producing.
 *
 * ONE OWNER AT A TIME, ON PURPOSE. The obvious next feature is "run every statement for August",
 * and it is deliberately not here: a batch is only useful once the statements can be DELIVERED,
 * and delivery needs email and object storage that do not exist yet (ROADMAP M3). Shipping a
 * button that produces 140 PDFs into the operator's downloads folder would be a worse answer than
 * the one thing an agency actually does today, which is produce a statement, read it, and send
 * it. What lands here first is the document itself; the schedule attaches to it later, and builds
 * the same one — `buildOwnerStatement` in core is what guarantees that.
 *
 * THE SELECTION LIVES IN THE URL, like the audit report's filter. A statement is something an
 * operator sends to a colleague to check before it goes out, reloads after fixing a receipt, and
 * comes back to when the owner rings about it. Component state survives none of the three.
 */
function OwnerStatementsPage() {
  const selection = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  /*
   * Which month is "now" — a NEW ZEALAND day, resolved once per render from the injected instant.
   *
   * The server runs in UTC, where an Auckland evening is already tomorrow for half the year. On
   * the 31st at 9pm that is the difference between defaulting to this month's statement and
   * defaulting to next month's, which would open on a blank page every month end — the single
   * busiest evening this screen has.
   */
  const today = nzDate(new Date());
  const period = selection.period ?? periodStartFor(today);
  const refSeq = selection.owner;

  const owners = useQuery(
    trpc.property.owners.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load owners' },
      staleTime: 60_000,
    }),
  );

  /*
   * Whose agency this is, for the masthead. The same query the account menu runs, so on any
   * session that has opened the menu this is a cache hit. A statement handed to an owner with no
   * agency name on it is a page of figures from nobody in particular.
   */
  const orgs = useQuery(trpc.auth.organisations.queryOptions(undefined, { staleTime: 60_000 }));
  const orgName = orgs.data?.find((org) => org.isCurrent)?.name ?? null;

  const query = useQuery({
    ...trpc.reports.ownerStatement.queryOptions(
      // Only ever called with an owner — the `enabled` below is what makes the non-null safe, and
      // the fallback exists because the options are built before it is checked.
      { refSeq: refSeq ?? 0, period },
      { meta: { errorTitle: 'We could not produce that statement' }, staleTime: 30_000 },
    ),
    enabled: refSeq !== undefined,
  });

  /*
   * The document itself, once it has arrived. Named `statement` rather than `document` because
   * the global of that name is a real thing on this page — `window.print()` is one line below.
   */
  const statement = query.data;

  /*
   * Recording that a statement was PRODUCED — not that one was looked at.
   *
   * A statement carries a number, and the number is pinned by the first copy that goes out: from
   * then on nothing can renumber what the owner is holding (see `recordStatementIssue` on the
   * server). That is why this hangs off the export and not off the query — opening an owner's
   * August to check it must not pin anything, and a query would, since TanStack re-runs them on
   * focus and retry.
   *
   * NOT AWAITED, AND ITS FAILURE DOES NOT STOP THE DOWNLOAD. The operator asked for a document;
   * losing the record of it is a real problem but it is ours, not theirs, and a failed audit write
   * that swallowed somebody's statement would be the worse of the two failures.
   */
  const recordIssue = useMutation(
    trpc.reports.recordStatementIssue.mutationOptions({
      meta: { errorTitle: 'Could not record that the statement was issued' },
    }),
  );

  return (
    <ReportWorkspace
      title="Owner statement"
      subtitle={
        statement === undefined
          ? `${formatPeriod(period)} — choose an owner`
          : [statementNumberLabel(statement), statement.owner.name, formatPeriod(period)]
              .filter((part) => part !== null)
              .join(' · ')
      }
      actions={
        <>
          {/*
           * TWO WAYS OUT, and on this document they are not interchangeable.
           *
           * PRINT is right for the copy that goes in the post and costs nothing to offer. EXPORT
           * produces the file — page-numbered, named by owner and month, and the thing that will
           * be attached to an email the moment there is a mailer to attach it to. A statement is
           * SENT, so the file is not a nicety here in the way it is on a report that is only
           * ever read on a screen — and the mailer now attaches THIS renderer's output, so the file an
           * owner receives and the file produced by this button are one document.
           * See `@promanage/documents/owner-statement-pdf`.
           */}
          <ExportPdfButton
            label="Download PDF"
            disabled={statement === undefined}
            onExport={async () => {
              if (statement === undefined) return;
              // The renderer, fetched by this click and never by the page view — see
              // DESIGN_SYSTEM.md §8 on why a PDF library is a lazy chunk or a mistake.
              const { exportOwnerStatementPdf } =
                await import('@promanage/documents/owner-statement-pdf');
              exportOwnerStatementPdf({
                statement,
                orgName,
                logo: await letterheadLogo(),
                generatedAt: new Date(),
              });

              // The file exists now, so the issue is a fact. See `recordIssue`.
              recordIssue.mutate({ refSeq: statement.owner.refSeq, period: statement.period });
            }}
          />

          <Button size="sm" onClick={() => window.print()} disabled={statement === undefined}>
            Print
          </Button>
        </>
      }
      controls={
        <Panel className="print:hidden">
          <PanelHeader
            title="Report selection"
            subtitle="One owner, one month — the month the books are locked and paid in."
          />
          <StatementPicker
            selection={selection}
            period={period}
            today={today}
            owners={owners.data ?? []}
            ownersPending={owners.isPending}
            onChange={(next) => {
              // `replace` so the back button leaves the screen rather than walking back through
              // every owner somebody stepped past on their way down the list.
              void navigate({ search: next, replace: true });
            }}
          />
        </Panel>
      }
    >
      <div className="flex flex-col gap-4">
        {owners.isError ? (
          <LoadFailed retry={() => void owners.refetch()} retrying={owners.isFetching} />
        ) : null}
        {query.isError ? (
          <LoadFailed retry={() => void query.refetch()} retrying={query.isFetching} />
        ) : null}

        {/*
         * WHO IT GOES TO — on the screen, never on the document.
         *
         * It answers the operator's next question rather than the owner's: the owner knows who
         * they are. An owner ENTITY does not answer the phone — "Solomona Holdings Ltd" has a
         * trustee or a director behind it — so an entity with nobody attached is a statement that
         * cannot be sent, and finding that out here beats finding it out when the mailer lands and
         * silently skips them.
         */}
        {statement === undefined ? null : (
          <Panel className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-5 py-3 print:hidden">
            <span className="text-[0.75rem] font-semibold tracking-[0.04em] text-ink-subtle uppercase">
              Goes to
            </span>
            {statement.owner.contacts.length === 0 ? (
              <span className="text-[0.8125rem] text-warn">
                Nobody is attached to {statement.owner.name}. Add a contact on the owner’s record
                before this can be sent.
              </span>
            ) : (
              <span className="text-[0.8125rem]">
                {statement.owner.contacts
                  .map((contact) => `${contact.name} (${ROLE_LABELS[contact.roleType]})`)
                  .join(', ')}
              </span>
            )}
          </Panel>
        )}

        {refSeq === undefined ? (
          <Panel className="px-5 py-10 text-center print:hidden">
            <h2 className="text-[0.9375rem] font-semibold">Choose an owner</h2>
            <p className="mx-auto mt-1 max-w-md text-[0.8125rem] text-ink-muted">
              A statement shows one owner their share of everything received, what was charged
              against it, and what is being held for them — running from the balance they started
              the month with.
            </p>
          </Panel>
        ) : query.isPending ? (
          <QueryLoading />
        ) : statement === undefined ? null : (
          <OwnerStatementReport
            statement={statement}
            orgName={orgName}
            // Read at render, not pinned: the produced-at line has to be the moment this copy was
            // taken off the system, and a statement left open on a screen overnight is a statement
            // whose figures may have moved.
            generatedAt={new Date()}
          />
        )}
      </div>
    </ReportWorkspace>
  );
}
